/**
 * What a flag on an attendance day MEANS, in words a reviewer reads rather than a code they decode.
 *
 * The screens used to print the stored value with its underscores swapped for spaces — "AUTO CHECKOUT",
 * "offline punch" — and painted every one of them red. That is wrong twice over: it shows the database's
 * word rather than the reader's, and it calls everything an accusation. Some flags are simply facts
 * about how the record was made; `tone: 'neutral'` marks those, and the caller keeps them out of the
 * "issues" count.
 *
 * Sales Performance keeps its own richer copy (it also carries filter hints); this is the plain list
 * the attendance register and the day record share.
 */
export type FlagTone = 'red' | 'gold' | 'neutral';

export interface FlagMeta {
  label: string;
  tone: FlagTone;
}

const FLAGS: Record<string, FlagMeta> = {
  PAID_ON_NO_WORK: { label: 'Paid, no work', tone: 'red' },
  NO_WORK_CLAIMED: { label: 'Claimed, no work', tone: 'red' },
  DISTANCE_MISMATCH: { label: 'Km mismatch', tone: 'red' },
  IMPOSSIBLE_TRAVEL: { label: 'Impossible travel', tone: 'red' },
  NO_WORK: { label: 'No work', tone: 'gold' },
  SHORT_DAY: { label: 'Short day', tone: 'gold' },
  FACE_MISMATCH: { label: 'Face mismatch', tone: 'gold' },
  OUTSIDE_GEOFENCE: { label: 'Outside geofence', tone: 'gold' },
  AUTO_CHECKOUT: { label: 'Auto check-out', tone: 'gold' },
  DISTANCE_UNVERIFIED: { label: 'Km unverified', tone: 'neutral' },
  NO_GPS: { label: 'No GPS', tone: 'neutral' },
  // Not a doubt about the rep: the phone had no signal, so it recorded the time itself and sent the
  // punch when it could. Without this the reader cannot tell why a punch reached the server late.
  OFFLINE_PUNCH: { label: 'Punched offline', tone: 'neutral' },
};

/** An unknown flag still reads as words — never as `SOME_NEW_CODE`. */
export const attendanceFlag = (f: string): FlagMeta =>
  FLAGS[f] ?? { label: f.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()), tone: 'gold' };

/** True for the flags that are a fact about the record, not something to answer for. */
export const isInformationalFlag = (f: string): boolean => attendanceFlag(f).tone === 'neutral';
